import type { ObservedPattern } from "@money/shared";
import {
  usePatterns,
  useRefreshPatterns,
  useUpdatePattern,
} from "../api/patterns";
import { Loading, ErrorState } from "../components/States";

const TYPE_LABEL: Record<string, string> = {
  category_trend: "Trend",
  subscription: "Subscription",
  lifestyle_creep: "Lifestyle creep",
  anomaly: "Anomaly",
  savings_rate: "Savings rate",
  tax_opportunity: "Tax opportunity",
};

export function Patterns() {
  const patterns = usePatterns();
  const refresh = useRefreshPatterns();
  const update = useUpdatePattern();

  const list = patterns.data?.patterns ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Observations</h1>
        <button
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="rounded border border-slate-700 hover:border-slate-500 text-sm px-3 py-1 disabled:opacity-50"
        >
          {refresh.isPending ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Things we noticed in your spending — observations, not advice.
        Acknowledge to mark as seen, or dismiss to hide for good.
      </p>

      {patterns.isPending && <Loading />}
      {patterns.isError && <ErrorState onRetry={() => patterns.refetch()} />}
      {!patterns.isPending && !patterns.isError && list.length === 0 && (
        <div className="text-slate-400 text-sm py-8 text-center">
          No observations yet. Once you've synced some history, hit Refresh.
        </div>
      )}

      <div className="space-y-2">
        {list.map((p) => (
          <PatternCard
            key={p.id}
            pattern={p}
            busy={update.isPending}
            onAcknowledge={() => update.mutate({ id: p.id, patch: { acknowledged: 1 } })}
            onDismiss={() => update.mutate({ id: p.id, patch: { dismissed: 1 } })}
          />
        ))}
      </div>
    </div>
  );
}

function PatternCard({
  pattern,
  busy,
  onAcknowledge,
  onDismiss,
}: {
  pattern: ObservedPattern;
  busy: boolean;
  onAcknowledge: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className={`rounded-lg border p-4 ${
        pattern.acknowledged ? "border-slate-800 bg-slate-900/30" : "border-slate-700 bg-slate-900/50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-wide rounded bg-slate-800 text-slate-400 px-1.5 py-0.5">
            {TYPE_LABEL[pattern.pattern_type] ?? pattern.pattern_type}
          </span>
          <p className="text-sm mt-2">{pattern.description}</p>
        </div>
        {pattern.acknowledged === 1 && (
          <span className="text-xs text-slate-500 whitespace-nowrap">seen</span>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        {pattern.acknowledged === 0 && (
          <button
            onClick={onAcknowledge}
            disabled={busy}
            className="rounded border border-slate-700 hover:border-slate-500 text-xs px-3 py-1 disabled:opacity-50"
          >
            Acknowledge
          </button>
        )}
        <button
          onClick={onDismiss}
          disabled={busy}
          className="rounded border border-slate-700 hover:border-rose-500 hover:text-rose-300 text-xs px-3 py-1 disabled:opacity-50"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
